// Start adventure view - character and friend selection before starting game

import { css, html } from 'lit';
import { api, getCDNBaseUrl } from '../../lib/api.js';
import { BaseComponent } from '../../lib/base-component.js';
import { router } from '../../lib/router.js';
import { debugState } from '../../state/debug-state.js';

// Signal icon SVG
const signalIcon = html`<svg
  xmlns="http://www.w3.org/2000/svg"
  width="14"
  height="14"
  viewBox="0 0 24 24"
  fill="none"
  stroke="currentColor"
  stroke-width="2"
  stroke-linecap="round"
  stroke-linejoin="round"
  class="me-1"
>
  <path d="M2 20h.01" />
  <path d="M7 20v-4" />
  <path d="M12 20v-8" />
  <path d="M17 20V8" />
  <path d="M22 4v16" />
</svg>`;

// Clock icon SVG
const clockIcon = html`<svg
  xmlns="http://www.w3.org/2000/svg"
  width="14"
  height="14"
  viewBox="0 0 24 24"
  fill="none"
  stroke="currentColor"
  stroke-width="2"
  stroke-linecap="round"
  stroke-linejoin="round"
  class="me-1"
>
  <circle cx="12" cy="12" r="10" />
  <polyline points="12 6 12 12 16 14" />
</svg>`;

// Users icon SVG
const usersIcon = html`<svg
  xmlns="http://www.w3.org/2000/svg"
  width="14"
  height="14"
  viewBox="0 0 24 24"
  fill="none"
  stroke="currentColor"
  stroke-width="2"
  stroke-linecap="round"
  stroke-linejoin="round"
  class="me-1"
>
  <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
  <circle cx="9" cy="7" r="4" />
  <path d="M22 21v-2a4 4 0 0 0-3-3.87" />
  <path d="M16 3.13a4 4 0 0 1 0 7.75" />
</svg>`;

// SquareUserRound icon SVG
const userIconMedium = html`<svg
  xmlns="http://www.w3.org/2000/svg"
  width="32"
  height="32"
  viewBox="0 0 24 24"
  fill="none"
  stroke="currentColor"
  stroke-width="1.5"
  stroke-linecap="round"
  stroke-linejoin="round"
>
  <path d="M18 21a6 6 0 0 0-12 0" />
  <circle cx="12" cy="11" r="4" />
  <rect width="18" height="18" x="3" y="3" rx="2" />
</svg>`;

// UserPlus icon SVG
const userPlusIcon = html`<svg
  xmlns="http://www.w3.org/2000/svg"
  width="18"
  height="18"
  viewBox="0 0 24 24"
  fill="none"
  stroke="currentColor"
  stroke-width="2"
  stroke-linecap="round"
  stroke-linejoin="round"
  class="me-2"
>
  <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
  <circle cx="9" cy="7" r="4" />
  <line x1="19" x2="19" y1="8" y2="14" />
  <line x1="22" x2="16" y1="11" y2="11" />
</svg>`;

// Check icon SVG
const checkIcon = html`<svg
  xmlns="http://www.w3.org/2000/svg"
  width="16"
  height="16"
  viewBox="0 0 24 24"
  fill="none"
  stroke="currentColor"
  stroke-width="2"
  stroke-linecap="round"
  stroke-linejoin="round"
  class="friend-check"
>
  <polyline points="20 6 9 17 4 12" />
</svg>`;

/**
 * Start adventure view component
 * Allows selecting a character and friends before starting a new adventure
 */
class StartAdventureView extends BaseComponent {
  static properties = {
    slug: { type: String },
    _adventure: { type: Object, state: true },
    _loading: { type: Boolean, state: true },
    _error: { type: String, state: true },
    _characters: { type: Array, state: true },
    _charactersLoading: { type: Boolean, state: true },
    _selectedCharacterId: { type: String, state: true },
    _friends: { type: Array, state: true },
    _friendsLoading: { type: Boolean, state: true },
    _selectedFriendIds: { type: Array, state: true },
    _starting: { type: Boolean, state: true },
    _startError: { type: String, state: true },
    _debugMode: { type: Boolean, state: true },
    _seedInput: { type: String, state: true },
    _replayLastSeed: { type: Boolean, state: true },
    _lastSeedForAdventure: { type: String, state: true },
  };

  static styles = css`
    :host {
      display: block;
    }

    .character-card {
      display: flex;
      align-items: center;
      gap: 12px;
      padding: 12px;
      border: 2px solid #dee2e6;
      border-radius: 8px;
      cursor: pointer;
      transition:
        border-color 0.15s,
        box-shadow 0.15s;
    }

    .character-card:hover:not(.disabled) {
      border-color: #0d6efd;
    }

    .character-card.disabled {
      opacity: 0.5;
      cursor: not-allowed;
      border-color: #dee2e6;
    }

    .in-game-badge {
      font-size: 0.7rem;
      padding: 2px 6px;
      border-radius: 4px;
      background-color: #6c757d;
      color: white;
      white-space: nowrap;
    }

    .character-card.selected {
      border-color: #0d6efd;
      box-shadow: 0 0 0 3px rgba(13, 110, 253, 0.25);
    }

    .character-image {
      width: 64px;
      height: 64px;
      border-radius: 8px;
      overflow: hidden;
      flex-shrink: 0;
    }

    .character-image img {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }

    .character-placeholder {
      width: 100%;
      height: 100%;
      background: #6c757d;
      display: flex;
      align-items: center;
      justify-content: center;
      color: white;
    }

    .character-info {
      flex-grow: 1;
      min-width: 0;
    }

    .character-name {
      font-weight: 600;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    .character-details {
      font-size: 0.875rem;
      color: #6c757d;
    }

    /* Friend card styles */
    .friend-card {
      display: flex;
      align-items: center;
      gap: 8px;
      padding: 8px 12px;
      border: 2px solid #dee2e6;
      border-radius: 20px;
      cursor: pointer;
      transition:
        border-color 0.15s,
        background-color 0.15s;
      user-select: none;
    }

    .friend-card:hover:not(.disabled) {
      border-color: #0d6efd;
    }

    .friend-card.selected {
      border-color: #0d6efd;
      background-color: rgba(13, 110, 253, 0.1);
    }

    .friend-card.disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }

    .friend-avatar {
      width: 28px;
      height: 28px;
      border-radius: 50%;
      object-fit: cover;
    }

    .friend-name {
      font-size: 0.875rem;
      font-weight: 500;
    }

    .friend-check {
      color: #0d6efd;
      margin-left: 4px;
    }
  `;

  constructor() {
    super();
    this.slug = '';
    this._adventure = null;
    this._loading = true;
    this._error = null;
    /** @type {Array<{id: string, name: string, level?: number, class?: string, tokenId?: string, activeGameId?: string}>} */
    this._characters = [];
    this._charactersLoading = true;
    /** @type {string | null} */
    this._selectedCharacterId = null;
    /** @type {Array<import('../../types/jsdoc-types.js').Friend>} */
    this._friends = [];
    this._friendsLoading = true;
    /** @type {Array<string>} */
    this._selectedFriendIds = [];
    this._starting = false;
    /** @type {string | null} */
    this._startError = null;
    /** @type {{dispose(): void, update(): void, _config: {title: string}} | null} */
    this._startTooltipInstance = null;
    // Debug mode gates the seed override field. Sourced from the shared
    // debugState singleton so a backtick toggle from the top bar flips
    // this view immediately without needing to navigate away and back.
    this._debugMode = debugState.enabled;
    /** @type {(() => void) | null} */
    this._unsubscribeDebug = null;
    // Empty string means "let the backend pick a random seed."
    this._seedInput = '';
    this._replayLastSeed = false;
    // The last seed used to start a game on this adventure, read
    // from localStorage when the slug becomes known. Saved on
    // successful start so the next session's "Replay last seed"
    // toggle can prefill the input. Empty string = no record.
    this._lastSeedForAdventure = '';
  }

  async connectedCallback() {
    await super.connectedCallback();
    this._debugMode = debugState.enabled;
    this._unsubscribeDebug = debugState.subscribe(() => {
      this._debugMode = debugState.enabled;
    });
    // Pull the most-recent seed used on this adventure (if any) so
    // the debug-mode "Replay last seed" toggle can prefill the
    // existing seed input.
    this._lastSeedForAdventure = this._readLastSeedForAdventure();
    Promise.all([this._loadAdventure(), this._loadCharacters(), this._loadFriends()]);
  }

  _lastSeedStorageKey() {
    return this.slug ? `d3-last-seed:${this.slug}` : '';
  }

  _readLastSeedForAdventure() {
    const key = this._lastSeedStorageKey();
    if (!key) return '';
    try {
      return localStorage.getItem(key) || '';
    } catch {
      return '';
    }
  }

  /** @param {Event} e */
  _handleReplayLastSeedToggle(e) {
    const checked = /** @type {HTMLInputElement} */ (e.target).checked;
    this._replayLastSeed = checked;
    if (checked && this._lastSeedForAdventure) {
      this._seedInput = this._lastSeedForAdventure;
    } else if (!checked) {
      this._seedInput = '';
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback?.();
    this._unsubscribeDebug?.();
    this._unsubscribeDebug = null;
    if (this._startTooltipInstance) {
      this._startTooltipInstance.dispose();
      this._startTooltipInstance = null;
    }
  }

  async _loadAdventure() {
    this._loading = true;
    this._error = null;

    try {
      const data =
        /** @type {Array<{id: string, slug: string, name: string, description: string, capsule_image_url: string, level: number, duration: string, players: string, maps: Array<object>, characters: Array<object>}>} */ (
          await api.getMarketplaceAdventures()
        );
      const found = data.find((/** @type {{slug: string}} */ a) => a.slug === this.slug);

      if (!found) {
        this._error = 'Adventure not found';
        return;
      }

      this._adventure = {
        id: found.id,
        slug: found.slug,
        name: found.name,
        description: found.description,
        capsuleImage: api.getImageUrl(found.capsule_image_url),
        difficulty: found.level ? `Level ${found.level}` : null,
        estimatedTime: found.duration,
        players: found.players,
        adventureData: {
          id: found.slug,
          name: found.name,
          description: found.description,
          maps: found.maps || [],
          characters: found.characters || [],
        },
      };
    } catch (err) {
      console.error('Failed to load adventure:', err);
      this._error = 'Failed to load adventure. Please try again.';
    } finally {
      this._loading = false;
    }
  }

  async _loadCharacters() {
    this._charactersLoading = true;

    try {
      this._characters =
        /** @type {Array<{id: string, name: string, level?: number, class?: string, tokenId?: string, activeGameId?: string}>} */ (
          await api.getCharacters()
        );
      // Prefer a character id provided via the query string (e.g. when
      // returning from the character creation flow). Fall back to
      // auto-selecting when exactly one character is available.
      const requestedId = router.getQuery().get('selectedCharacterId');
      const requested = requestedId ? this._characters.find((c) => c.id === requestedId && !c.activeGameId) : null;
      if (requested) {
        this._selectedCharacterId = requested.id;
      } else {
        const available = this._characters.filter((c) => !c.activeGameId);
        if (available.length === 1 && available[0]) {
          this._selectedCharacterId = available[0].id;
        }
      }
    } catch (err) {
      console.error('Failed to load characters:', err);
    } finally {
      this._charactersLoading = false;
    }
  }

  async _loadFriends() {
    this._friendsLoading = true;

    try {
      this._friends = /** @type {Array<import('../../types/jsdoc-types.js').Friend>} */ (await api.getFriends());
    } catch (err) {
      console.error('Failed to load friends:', err);
    } finally {
      this._friendsLoading = false;
    }
  }

  /** @param {string} friendId */
  _toggleFriend(friendId) {
    const index = this._selectedFriendIds.indexOf(friendId);
    if (index === -1) {
      // Add if not at max (3 friends)
      if (this._selectedFriendIds.length < 3) {
        this._selectedFriendIds = [...this._selectedFriendIds, friendId];
      }
    } else {
      // Remove
      this._selectedFriendIds = this._selectedFriendIds.filter((id) => id !== friendId);
    }
  }

  /** @param {string} emailHash */
  _getGravatarUrl(emailHash) {
    return `https://www.gravatar.com/avatar/${emailHash}?d=identicon&s=80`;
  }

  /** @param {{tokenId?: string}} character */
  _getCharacterTokenImage(character) {
    if (!character.tokenId || !getCDNBaseUrl()) return null;
    return `${getCDNBaseUrl()}/token/${character.tokenId}`;
  }

  /** @param {{id: string, activeGameId?: string}} character */
  _selectCharacter(character) {
    if (character.activeGameId) return;
    this._selectedCharacterId = character.id;
  }

  /** @param {Event} e */
  _handleSeedInput(e) {
    const target = /** @type {HTMLInputElement} */ (e.target);
    this._seedInput = target.value;
  }

  async _startGame() {
    if (!this._selectedCharacterId || !this._adventure) return;

    this._starting = true;
    this._startError = null;

    try {
      const invitedUserIds = this._selectedFriendIds.length > 0 ? this._selectedFriendIds : undefined;

      // In debug mode, forward the override seed as an int64 if the user
      // typed one. Empty string or non-numeric input falls through to
      // backend-generated randomness.
      /** @type {{adventureId: string, characterId: string, invitedUserIds?: string[], seed?: number}} */
      const payload = {
        adventureId: this._adventure.slug,
        characterId: this._selectedCharacterId,
        invitedUserIds,
      };
      if (this._debugMode && this._seedInput.trim() !== '') {
        const parsed = Number(this._seedInput.trim());
        if (!Number.isFinite(parsed) || !Number.isInteger(parsed)) {
          this._startError = 'Seed must be an integer.';
          this._starting = false;
          return;
        }
        payload.seed = parsed;
      }

      const result = /** @type {{id: string, seed?: number}} */ (await api.createGame(payload));

      // Save the seed used so the next session's "Replay last seed"
      // toggle can prefill. Prefer the explicit override when set;
      // fall back to the seed echoed by the server response if it
      // includes one.
      try {
        const key = this._lastSeedStorageKey();
        if (key) {
          if (payload.seed !== undefined) {
            localStorage.setItem(key, String(payload.seed));
          } else if (typeof result.seed === 'number' && Number.isFinite(result.seed)) {
            localStorage.setItem(key, String(result.seed));
          }
        }
      } catch {
        /* localStorage unavailable — the toggle just won't have a value next time */
      }

      router.navigate(`/game/${result.id}`);
    } catch (err) {
      console.error('Failed to start adventure:', err);
      this._startError = /** @type {Error} */ (err).message || 'Failed to start adventure. Please try again.';
    } finally {
      this._starting = false;
    }
  }

  _goBackToAdventures() {
    router.navigate('/adventures');
  }

  _goToCreateCharacter() {
    // Pass the current start-adventure URL as `returnTo` so that the
    // character creation flow sends the player back here (with the new
    // character pre-selected) after they finish creating.
    const returnTo = encodeURIComponent(`/adventures/${this.slug}/start`);
    router.navigate(`/characters/new?skipRestore=true&returnTo=${returnTo}`);
  }

  get _startButtonTooltip() {
    if (!this._selectedCharacterId) return 'Select a character to continue';
    return '';
  }

  /** @param {Map<string, unknown>} changed */
  updated(changed) {
    super.updated?.(changed);
    this._updateStartButtonTooltip();
  }

  _updateStartButtonTooltip() {
    const tooltipText = this._startButtonTooltip;
    const el = this.renderRoot?.querySelector('[data-start-tooltip]');

    if (!tooltipText || !el) {
      if (this._startTooltipInstance) {
        this._startTooltipInstance.dispose();
        this._startTooltipInstance = null;
      }
      return;
    }

    const Tooltip = /** @type {any} */ (window).bootstrap?.Tooltip;
    if (!Tooltip) return;

    if (this._startTooltipInstance) {
      this._startTooltipInstance._config.title = tooltipText;
      this._startTooltipInstance.update();
    } else {
      this._startTooltipInstance = new Tooltip(el, {
        title: tooltipText,
        placement: 'bottom',
        container: 'body',
      });
    }
  }

  render() {
    return html`
      ${
        this._loading
          ? html`
            <div class="text-center py-5">
              <div class="spinner-border" role="status">
                <span class="visually-hidden">Loading...</span>
              </div>
              <p class="mt-3 text-muted">Loading adventure...</p>
            </div>
          `
          : this._error
            ? html` <div class="alert alert-danger" role="alert">${this._error}</div> `
            : this._adventure
              ? html`
                <div class="row">
                  <!-- Adventure Details Column -->
                  <div class="col-lg-5 mb-4">
                    <div class="card">
                      ${
                        this._adventure.capsuleImage
                          ? html`
                            <div
                              class="card-img-top"
                              style="height: 200px; background-size: cover; background-position: center; background-image: url('${
                                this._adventure.capsuleImage
                              }');"
                            ></div>
                          `
                          : html`
                            <div
                              class="card-img-top d-flex align-items-center justify-content-center"
                              style="height: 200px; background: linear-gradient(135deg, #2d3561 0%, #c05c7e 100%);"
                            >
                              <h2 class="text-white fw-bold text-center px-3">${this._adventure.name}</h2>
                            </div>
                          `
                      }

                      <div class="card-body">
                        <h3 class="card-title">${this._adventure.name}</h3>
                        <p class="card-text text-muted">${this._adventure.description}</p>

                        <div class="d-flex gap-3 text-muted small">
                          ${
                            this._adventure.difficulty
                              ? html` <span>${signalIcon}${this._adventure.difficulty}</span> `
                              : ''
                          }
                          ${
                            this._adventure.estimatedTime
                              ? html` <span>${clockIcon}${this._adventure.estimatedTime}</span> `
                              : ''
                          }
                          ${
                            this._adventure.players
                              ? html` <span>${usersIcon}${this._adventure.players} players</span> `
                              : ''
                          }
                        </div>
                      </div>
                    </div>
                  </div>

                  <!-- Character Selection Column -->
                  <div class="col-lg-7">
                    <div class="d-flex justify-content-between align-items-center mb-3">
                      <h4 class="mb-0">Select Your Character</h4>
                      ${
                        this._characters.length > 0
                          ? html`
                            <span class="d-inline-block" data-start-tooltip>
                              <button
                                class="btn btn-primary"
                                ?disabled="${!this._selectedCharacterId || this._starting}"
                                @click="${this._startGame}"
                              >
                                ${
                                  this._starting
                                    ? html`<span class="spinner-border spinner-border-sm me-2"></span>`
                                    : ''
                                }
                                ${this._starting ? 'Starting...' : 'Start Adventure'}
                              </button>
                            </span>
                          `
                          : ''
                      }
                    </div>

                    ${
                      this._charactersLoading
                        ? html`
                          <div class="text-center py-4">
                            <div class="spinner-border spinner-border-sm" role="status">
                              <span class="visually-hidden">Loading characters...</span>
                            </div>
                            <p class="mt-2 text-muted small">Loading your characters...</p>
                          </div>
                        `
                        : this._characters.length === 0
                          ? html`
                            <div class="alert alert-warning">
                              <p class="mb-2">You don't have any characters yet.</p>
                              <button class="btn btn-primary btn-sm" @click="${this._goToCreateCharacter}">
                                Create a Character
                              </button>
                            </div>
                          `
                          : html`
                            <div class="row row-cols-1 row-cols-sm-2 row-cols-xl-3 g-3 mb-4">
                              <div class="col">
                                <div class="character-card" @click="${this._goToCreateCharacter}">
                                  <div class="character-image">
                                    <div class="character-placeholder" style="font-size:2rem;color:#0d6efd;background:rgba(13,110,253,0.1);border:2px dashed #0d6efd;border-radius:8px;">+</div>
                                  </div>
                                  <div class="character-info">
                                    <div class="character-name">New Character</div>
                                    <div class="character-details">Create a new character</div>
                                  </div>
                                </div>
                              </div>
                              ${this._characters.map(
                                (character) => html`
                                  <div class="col">
                                    <div
                                      class="character-card ${
                                        this._selectedCharacterId === character.id ? 'selected' : ''
                                      } ${character.activeGameId ? 'disabled' : ''}"
                                      @click="${() => this._selectCharacter(character)}"
                                    >
                                      <div class="character-image">
                                        ${
                                          this._getCharacterTokenImage(character)
                                            ? html` <img src="${this._getCharacterTokenImage(character)}" alt="Token" /> `
                                            : html` <div class="character-placeholder">${userIconMedium}</div> `
                                        }
                                      </div>
                                      <div class="character-info">
                                        <div class="character-name">${character.name}</div>
                                        <div class="character-details">Level ${character.level} ${character.class}</div>
                                        ${
                                          character.activeGameId
                                            ? html` <span class="in-game-badge">In Active Game</span> `
                                            : ''
                                        }
                                      </div>
                                    </div>
                                  </div>
                                `,
                              )}
                            </div>
                          `
                    }

                    <!-- Friend Selection -->
                    ${
                      this._characters.length > 0
                        ? html`
                          <div class="mb-4">
                            <h5 class="mb-3">${userPlusIcon} Invite Friends (Optional)</h5>
                            <p class="text-muted small mb-3">
                              Select up to 3 friends to join your adventure. They'll receive an invitation to join.
                            </p>

                            ${
                              this._friendsLoading
                                ? html`
                                  <div class="text-center py-3">
                                    <div class="spinner-border spinner-border-sm" role="status">
                                      <span class="visually-hidden">Loading friends...</span>
                                    </div>
                                    <span class="ms-2 text-muted small">Loading friends...</span>
                                  </div>
                                `
                                : this._friends.length === 0
                                  ? html`
                                    <div class="text-muted small">
                                      <p class="mb-0">
                                        No friends to invite. Add friends from your profile to play together!
                                      </p>
                                    </div>
                                  `
                                  : html`
                                    <div class="row row-cols-1 row-cols-sm-2 row-cols-xl-3 row-cols-xxl-4 g-3">
                                      ${this._friends.map(
                                        (friend) => html`
                                          <div class="col">
                                            <div
                                              class="friend-card ${
                                                this._selectedFriendIds.includes(friend.id) ? 'selected' : ''
                                              } ${
                                                !this._selectedFriendIds.includes(friend.id) &&
                                                this._selectedFriendIds.length >= 3
                                                  ? 'disabled'
                                                  : ''
                                              }"
                                              @click="${() => this._toggleFriend(friend.id)}"
                                            >
                                              <img
                                                src="${friend.picture || this._getGravatarUrl(friend.emailHash || '')}"
                                                alt="${friend.username}"
                                                class="friend-avatar"
                                              />
                                              <span class="friend-name">${friend.username}</span>
                                              ${this._selectedFriendIds.includes(friend.id) ? checkIcon : ''}
                                            </div>
                                          </div>
                                        `,
                                      )}
                                    </div>
                                  `
                            }
                            ${
                              this._selectedFriendIds.length > 0
                                ? html`
                                  <div class="mt-2 text-muted small">
                                    ${this._selectedFriendIds.length}/3 friends selected
                                  </div>
                                `
                                : ''
                            }
                          </div>
                        `
                        : ''
                    }
                    ${
                      this._debugMode
                        ? html`
                          <div class="mt-4 p-3" style="background:#2a1f0f;border:1px dashed #b08040;border-radius:8px;">
                            <label for="seed-input" class="form-label text-warning fw-bold mb-1">
                              Debug · RNG seed
                            </label>
                            <div class="text-muted small mb-2">
                              Leave blank for a random seed. Any int64 produces a fully reproducible game.
                            </div>
                            <input
                              id="seed-input"
                              type="text"
                              inputmode="numeric"
                              class="form-control"
                              placeholder="e.g. 12345"
                              .value="${this._seedInput}"
                              @input="${this._handleSeedInput}"
                            />
                            ${
                              this._lastSeedForAdventure
                                ? html`
                                  <label class="d-flex align-items-center gap-2 mt-2 mb-0 text-warning small" style="cursor:pointer;">
                                    <input
                                      type="checkbox"
                                      .checked="${this._replayLastSeed}"
                                      @change="${this._handleReplayLastSeedToggle}"
                                    />
                                    <span>
                                      Replay last seed (<code>${this._lastSeedForAdventure}</code>)
                                    </span>
                                  </label>
                                `
                                : ''
                            }
                          </div>
                        `
                        : ''
                    }
                    ${this._startError ? html` <div class="alert alert-danger mt-3">${this._startError}</div> ` : ''}
                  </div>
                </div>
              `
              : ''
      }
    `;
  }
}

customElements.define('start-adventure-view', StartAdventureView);
